import React, { useState, useEffect } from 'react';

type DemoStep = {
  id: string;
  title: string;
  slackMessage: string;
  webAction?: string;
  result?: string;
  duration: number; // milliseconds
};

const TaskTapLiveDemo = () => {
  const [currentScenario, setCurrentScenario] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [slackMessages, setSlackMessages] = useState<string[]>([]);
  const [webView, setWebView] = useState<string>('');
  const [metrics, setMetrics] = useState({
    tasksPosted: 0,
    tasksClaimed: 0,
    tasksCompleted: 0,
    avgTimeToClaimMinutes: 2,
    avgCompletionHours: 4,
    totalPaid: 0,
    workerSatisfaction: 98
  });

  const scenarios = [
    {
      name: 'Happy Path: Task Completion',
      emoji: '✅',
      steps: [
        {
          id: 'post',
          title: 'Company posts task',
          slackMessage: '📢 NEW TASK: Design landing page hero section\n💰 $50 | ⏰ 6 hours\n👉 Click to claim',
          duration: 2000
        },
        {
          id: 'claim',
          title: 'Designer claims task',
          slackMessage: '📋 Task claimed by Sarah_Designer',
          webAction: '✅ Confirmation: You claimed the task!',
          duration: 2000
        },
        {
          id: 'work',
          title: 'Designer works on task',
          slackMessage: '⏳ Sarah_Designer is working on: Design landing page',
          duration: 3000
        },
        {
          id: 'complete',
          title: 'Designer submits work',
          slackMessage: '✅ Task completed by Sarah_Designer\n🔍 Pending review',
          webAction: '📤 Work submitted successfully!',
          duration: 2000
        },
        {
          id: 'review',
          title: 'Manager approves',
          slackMessage: '✅ APPROVED: Design landing page\n💰 $50 paid to Sarah_Designer',
          webAction: '💰 Payment processed!',
          result: 'Task completed in 4 hours. Designer earned $50.',
          duration: 3000
        }
      ]
    },
    {
      name: 'Recovery: Accidental Claim',
      emoji: '↩️',
      steps: [
        {
          id: 'post',
          title: 'Task posted',
          slackMessage: '📢 NEW TASK: Write API documentation\n💰 $30 | ⏰ 3 hours',
          duration: 2000
        },
        {
          id: 'wrong-claim',
          title: 'Wrong person claims',
          slackMessage: '📋 Task claimed by John_Backend',
          webAction: '⚠️ Are you sure? This is a writing task.',
          duration: 2000
        },
        {
          id: 'realize',
          title: 'Worker realizes mistake',
          slackMessage: '⚠️ John_Backend claimed by mistake\n↩️ UNDO available for 30 seconds',
          webAction: 'Undo timer: 28... 27... 26...',
          duration: 2000
        },
        {
          id: 'undo',
          title: 'Worker clicks undo',
          slackMessage: '↩️ Claim UNDONE by John_Backend\n📢 Task available again!',
          webAction: '✅ Successfully undone!',
          duration: 2000
        },
        {
          id: 'reclaim',
          title: 'Right person claims',
          slackMessage: '📋 Task claimed by Emma_TechWriter',
          result: 'Mistake corrected! Right person got the task.',
          duration: 2000
        }
      ]
    },
    {
      name: 'Scale: Multiple Tasks',
      emoji: '🚀',
      steps: [
        {
          id: 'batch',
          title: 'Company posts batch',
          slackMessage: '📢 5 NEW TASKS POSTED:\n• Design icons - $20\n• Write copy - $30\n• Test feature - $25\n• Review PR - $15\n• Update docs - $20',
          duration: 2000
        },
        {
          id: 'claims',
          title: 'Workers claim tasks',
          slackMessage: '📋 Design icons → claimed by Alex\n📋 Write copy → claimed by Emma\n📋 Test feature → claimed by Jordan',
          webAction: '3 tasks claimed in < 1 minute!',
          duration: 3000
        },
        {
          id: 'progress',
          title: 'Parallel progress',
          slackMessage: '⚡ 3 tasks in progress\n👥 3 workers active\n⏱️ Avg time to claim: 45 seconds',
          duration: 2000
        },
        {
          id: 'completions',
          title: 'Tasks completing',
          slackMessage: '✅ Design icons - COMPLETE\n✅ Write copy - COMPLETE\n⏳ Test feature - IN REVIEW',
          webAction: '📊 60% completion in 2 hours',
          duration: 3000
        },
        {
          id: 'payments',
          title: 'Batch payments',
          slackMessage: '💰 PAYMENTS SENT:\n• Alex: $20\n• Emma: $30\n• Jordan: $25 (pending)',
          result: '$75 distributed to 3 workers in 2 hours',
          duration: 3000
        }
      ]
    },
    {
      name: 'Support: Worker Gets Help',
      emoji: '🆘',
      steps: [
        {
          id: 'stuck',
          title: 'Worker stuck on task',
          slackMessage: '⚠️ Jordan_Dev working on: Deploy to AWS\n⏰ 2 hours elapsed',
          duration: 2000
        },
        {
          id: 'help',
          title: 'Worker requests help',
          slackMessage: '🆘 HELP REQUEST from Jordan_Dev\nTask: Deploy to AWS\nIssue: Permission errors',
          webAction: '📝 Support ticket #H-1234 created',
          duration: 2000
        },
        {
          id: 'support',
          title: 'Support responds',
          slackMessage: '💬 Support → Jordan_Dev: Check IAM roles, sending guide',
          webAction: '📚 AWS Deploy Guide opened',
          duration: 3000
        },
        {
          id: 'resolved',
          title: 'Issue resolved',
          slackMessage: '✅ Jordan_Dev completed: Deploy to AWS\n👍 Support ticket closed',
          result: 'Worker unblocked, task completed successfully',
          duration: 2000
        }
      ]
    }
  ];

  useEffect(() => {
    if (!isPlaying) return;

    const scenario = scenarios[currentScenario];
    const step = scenario.steps[currentStep];

    // Add Slack message
    setSlackMessages(prev => [...prev, step.slackMessage]);

    // Show web action if exists
    if (step.webAction) {
      setWebView(step.webAction);
    }

    // Update metrics
    if (step.id === 'post' || step.id === 'batch') {
      setMetrics(prev => ({ ...prev, tasksPosted: prev.tasksPosted + (step.id === 'batch' ? 5 : 1) }));
    }
    if (step.id === 'claim' || step.id === 'claims') {
      setMetrics(prev => ({ ...prev, tasksClaimed: prev.tasksClaimed + (step.id === 'claims' ? 3 : 1) }));
    }
    if (step.id === 'complete' || step.id === 'completions') {
      setMetrics(prev => ({ ...prev, tasksCompleted: prev.tasksCompleted + (step.id === 'completions' ? 2 : 1) }));
    }
    if (step.id === 'review' || step.id === 'payments') {
      setMetrics(prev => ({ ...prev, totalPaid: prev.totalPaid + (step.id === 'payments' ? 75 : 50) }));
    }

    const timer = setTimeout(() => {
      if (currentStep < scenario.steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        setIsPlaying(false);
      }
    }, step.duration);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, currentScenario]);

  const startScenario = (index: number) => {
    setCurrentScenario(index);
    setCurrentStep(0);
    setSlackMessages([]);
    setWebView('');
    setIsPlaying(true);
  };

  const currentScenarioData = scenarios[currentScenario];
  const currentStepData = currentScenarioData.steps[currentStep];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-4">TaskTap Live Demo</h1>
          <p className="text-xl text-gray-400">Watch how TaskTap transforms task management</p>
        </div>

        {/* Scenario Selector */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {scenarios.map((scenario, idx) => (
            <button
              key={idx}
              onClick={() => startScenario(idx)}
              className={`p-4 rounded-lg border transition-all ${
                currentScenario === idx
                  ? 'bg-blue-900/50 border-blue-500'
                  : 'bg-gray-800 border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="text-3xl mb-2">{scenario.emoji}</div>
              <div className="text-sm font-semibold">{scenario.name}</div>
            </button>
          ))}
        </div>

        {/* Main Demo Area */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Slack Simulation */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center mb-4">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <h2 className="text-xl font-bold">#general - Slack</h2>
            </div>
            <div className="bg-black rounded p-4 h-96 overflow-y-auto space-y-3">
              {slackMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className="text-sm p-3 bg-gray-900 rounded border-l-4 border-blue-500 animate-slideIn"
                >
                  <pre className="whitespace-pre-wrap text-gray-300">{msg}</pre>
                </div>
              ))}
              {isPlaying && (
                <div className="text-gray-500 animate-pulse">
                  <span>typing...</span>
                </div>
              )}
            </div>
          </div>

          {/* Web View */}
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center mb-4">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <h2 className="text-xl font-bold">futr.bet/tasktap - Browser</h2>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-black rounded p-8 h-96 flex items-center justify-center">
              {webView ? (
                <div className="text-center animate-fadeIn">
                  <div className="text-6xl mb-4">🌐</div>
                  <p className="text-xl text-green-400">{webView}</p>
                </div>
              ) : (
                <p className="text-gray-600">Web actions appear here</p>
              )}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        {isPlaying && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">
                Step {currentStep + 1} of {currentScenarioData.steps.length}
              </span>
              <span className="text-sm font-semibold">{currentStepData.title}</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${((currentStep + 1) / currentScenarioData.steps.length) * 100}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Metrics Dashboard */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-3xl font-bold text-blue-400">{metrics.tasksPosted}</div>
            <div className="text-sm text-gray-400">Tasks Posted</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-3xl font-bold text-green-400">{metrics.tasksClaimed}</div>
            <div className="text-sm text-gray-400">Tasks Claimed</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-3xl font-bold text-yellow-400">${metrics.totalPaid}</div>
            <div className="text-sm text-gray-400">Total Paid</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="text-3xl font-bold text-purple-400">{metrics.workerSatisfaction}%</div>
            <div className="text-sm text-gray-400">Satisfaction</div>
          </div>
        </div>

        {/* Result Message */}
        {!isPlaying && currentStepData?.result && (
          <div className="bg-green-900/30 border border-green-500 rounded-lg p-6 text-center animate-fadeIn">
            <p className="text-xl">{currentStepData.result}</p>
          </div>
        )}

        {/* Play Controls */}
        <div className="text-center">
          <button
            onClick={() => startScenario(currentScenario)}
            disabled={isPlaying}
            className="px-8 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 rounded-lg font-semibold transition-colors"
          >
            {isPlaying ? 'Playing...' : 'Replay Scenario'}
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-slideIn {
          animation: slideIn 0.3s ease-out;
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TaskTapLiveDemo;